import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, ShieldCheck, Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useStock } from "@/context/StockContext";
import {
  computeDiscountedPrice,
  formatPrice,
  getProductDiscountPct,
  isProductNew,
  localized,
  productSlug,
} from "@/data/products";
import { resolveAssetUrl } from "@/lib/assetUrl";
import StoreImg from "@/components/store/StoreImg";
import { trackEvent } from "@/lib/analytics";

const RETURN_KEY = "bob_store_return";

const saveStoreReturnPosition = (productId, source = "grid") => {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path !== "/stock" && path !== "/stock/shein") return;
  try {
    sessionStorage.setItem(
      RETURN_KEY,
      JSON.stringify({
        productId,
        scrollY: window.scrollY || window.pageYOffset || 0,
        source,
        origin: path,
        ts: Date.now(),
      })
    );
  } catch {
    // Ignore quota / privacy mode errors; graceful fallback to top of page.
  }
};

export default function ProductCard({ product, highlight = false, source = "grid" }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const S = t.store;
  const { addToCart, openCart } = useCart();
  const { isProductOutOfStock, isSizeOutOfStock } = useStock();

  const name = localized(product.name, lang);
  const color = product.color ? localized(product.color, lang) : "";
  const discountPct = getProductDiscountPct(product);
  const discounted = computeDiscountedPrice(product.originalPrice, discountPct);
  const priceTBD = product.originalPrice == null;
  const outOfStock = isProductOutOfStock(product.id);
  const available = !outOfStock;
  const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
  const hasMultipleSizes = hasSizes && product.sizes.length > 1;
  const coverImage = resolveAssetUrl(product.images?.[0]);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [product.id, coverImage]);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!available || priceTBD) {
      trackEvent("product_add_to_cart_failed", {
        routeName: product.category === "shein" ? "shein" : "store",
        product: {
          id: product.id,
          title: name,
          category: product.category,
          sheinCategory: product.sheinCategory,
        },
        metadata: { source, reason: !available ? "out_of_stock" : "price_tbd" },
      });
      return;
    }
    if (hasMultipleSizes) {
      saveStoreReturnPosition(product.id, source);
      navigate(`/stock/${productSlug(product.id)}`);
      return;
    }
    if (hasSizes) {
      const onlySize = product.sizes[0];
      if (isSizeOutOfStock(product.id, onlySize.label)) {
        trackEvent("product_add_to_cart_failed", {
          routeName: product.category === "shein" ? "shein" : "store",
          product: {
            id: product.id,
            title: name,
            category: product.category,
            sheinCategory: product.sheinCategory,
          },
          metadata: { source, reason: "variant_out_of_stock", size: onlySize.label },
        });
        return;
      }
      trackEvent("product_add_to_cart_click", {
        routeName: product.category === "shein" ? "shein" : "store",
        product: {
          id: product.id,
          title: name,
          category: product.category,
          sheinCategory: product.sheinCategory,
        },
        metadata: { source, size: onlySize.label },
      });
      addToCart(product.id, 1, onlySize.label);
    } else {
      trackEvent("product_add_to_cart_click", {
        routeName: product.category === "shein" ? "shein" : "store",
        product: {
          id: product.id,
          title: name,
          category: product.category,
          sheinCategory: product.sheinCategory,
        },
        metadata: { source },
      });
      addToCart(product.id, 1, "");
    }
    trackEvent("product_add_to_cart_success", {
      routeName: product.category === "shein" ? "shein" : "store",
      product: {
        id: product.id,
        title: name,
        category: product.category,
        sheinCategory: product.sheinCategory,
      },
      metadata: { source },
    });
    openCart();
  };

  return (
    <Link
      to={`/stock/${productSlug(product.id)}`}
      onClick={() => saveStoreReturnPosition(product.id, source)}
      className="group block h-full rounded-[26px] transition-transform duration-300 hover:-translate-y-1"
      style={
        highlight
          ? {
              boxShadow:
                "0 0 0 3px #F37021, 0 0 0 7px rgba(243,112,33,0.18), 0 14px 36px rgba(243,112,33,0.25)",
            }
          : undefined
      }
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-[26px] border border-black/6 bg-white shadow-[0_18px_45px_rgba(16,24,40,0.06)] transition-all duration-300 group-hover:border-[#F37021]/20 group-hover:shadow-[0_24px_60px_rgba(16,24,40,0.1)] dark:border-white/10 dark:bg-[#121212] dark:group-hover:bg-[#1B1B1B]">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#111]">
          {!coverImage || imageError ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[radial-gradient(circle_at_top,rgba(243,112,33,0.22),transparent_42%),linear-gradient(180deg,#141414_0%,#232323_100%)] px-5 text-center text-white">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-[#F37021]">
                <ShoppingBag className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/60">
                  {S.pageTitle}
                </div>
                <div className="line-clamp-2 text-sm font-medium leading-5">
                  {name}
                </div>
              </div>
            </div>
          ) : (
            <StoreImg
              src={coverImage}
              alt={name}
              width={480}
              loading="lazy"
              onError={() => setImageError(true)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.045]"
            />
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/38 via-black/8 to-transparent" />

          <div className="absolute left-3 right-3 top-3 flex items-start justify-between gap-2">
            <div className="flex max-w-[70%] flex-wrap gap-1.5">
              {product.authentic && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#F3D571] to-[#C99A2E] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A0E00] shadow-sm"
                  data-testid={`authentic-badge-${product.id}`}
                >
                  <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                  {S.badgeAuthentic}
                </span>
              )}
              {isProductNew(product) && (
                <span
                  className="rounded-full bg-[#F37021] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
                  data-testid={`new-badge-${product.id}`}
                >
                  {S.badgeNew}
                </span>
              )}
              {product.tags?.includes("limited") && (
                <span className="rounded-full bg-white/92 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#111] shadow-sm backdrop-blur">
                  {S.badgeLimited}
                </span>
              )}
              {priceTBD && (
                <span
                  className="rounded-full border border-[#F37021]/30 bg-[#FFF3EA] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F37021]"
                  data-testid={`price-tbd-badge-${product.id}`}
                >
                  {S.priceTBD}
                </span>
              )}
            </div>

            {!priceTBD && (
              <span
                className="rounded-full bg-black/72 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur"
                data-testid={`discount-badge-${product.id}`}
              >
                -{discountPct}%
              </span>
            )}
          </div>

          {!available && !priceTBD && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/55">
              <span
                className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur sm:text-sm"
                data-testid={`out-of-stock-badge-${product.id}`}
              >
                {S.outOfStock}
              </span>
            </div>
          )}

          {available && !priceTBD && (
            <button
              type="button"
              onClick={handleAdd}
              className="absolute bottom-3 left-3 right-3 hidden h-11 translate-y-2 items-center justify-center gap-2 rounded-full bg-white text-sm font-semibold text-[#111] opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100 hover:bg-[#F37021] hover:text-white md:flex"
              data-testid={`product-card-add-${product.id}`}
              aria-label={hasMultipleSizes ? S.selectSize : S.addToCart}
            >
              <Plus className="h-4 w-4" />
              {hasMultipleSizes ? S.selectSize : S.addToCart}
            </button>
          )}
        </div>

        <div className="flex flex-1 flex-col p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#888] dark:text-[#8FA1B8]">
                #{product.id}
              </span>
            <span className="rounded-full bg-[#FFF6EF] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#F37021] dark:bg-[#1A1A1A] dark:text-[#F8A056]">
              {S.categories[product.category]}
            </span>
          </div>

          <div className="mt-3 min-h-[104px]">
            <h3 className="font-display text-base font-semibold leading-tight text-[#111] line-clamp-2 sm:text-lg dark:text-white">
              {name}
            </h3>
            {color && (
              <div className="mt-2 text-xs text-[#666] line-clamp-1 dark:text-[#BAC4D4]" data-testid={`color-${product.id}`}>
                {S.colorLabel}: {color}
              </div>
            )}
            {hasMultipleSizes && (
              <div className="mt-1.5 text-[11px] text-[#888] dark:text-[#8FA1B8]">
                {product.sizes.length} {S.sizesAvailable}
              </div>
            )}
          </div>

          <div className="mt-3 flex min-h-[48px] flex-wrap items-end gap-2.5">
            {priceTBD ? (
              <span className="text-sm font-bold text-[#F37021]">{S.priceTBD}</span>
            ) : (
              <>
                <span className="text-base font-bold text-[#F37021] sm:text-lg">
                  {formatPrice(discounted)}
                </span>
                <span className="text-sm text-[#999] line-through dark:text-[#8FA1B8]">
                  {formatPrice(product.originalPrice)}
                </span>
              </>
            )}
          </div>

          <div className="mt-auto pt-4">
            {priceTBD ? (
              <div className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-[#F37021]/30 bg-[#FFF3EA] px-3 text-center text-xs font-semibold leading-tight text-[#F37021] md:hidden">
                {S.priceTBDFull}
              </div>
            ) : available ? (
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#111] text-sm font-semibold text-white transition-colors hover:bg-[#F37021] md:hidden dark:bg-[#16253B]"
                data-testid={`product-card-add-mobile-${product.id}`}
              >
                <ShoppingBag className="h-4 w-4" />
                {hasMultipleSizes ? S.selectSize : S.addToCart}
              </button>
            ) : (
              <div className="inline-flex h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-[#F5F5F5] text-sm font-semibold text-[#999] md:hidden dark:bg-white/10 dark:text-[#8FA1B8]">
                {S.outOfStock}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
